/**
 * Pilotage — Dashboard exécutif IA cross-modules.
 * KPI · Health score · Charts CA 12m · Trésorerie 60j · AI Insight hebdo · AI Chat.
 */
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area,
} from "recharts";
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, Loader2,
             Send, Zap, Activity, Euro, Users, Bell } from "lucide-react";
import { APP_NAME } from "@/lib/branding";
import { toast } from "sonner";

const fmt = (n) => (n == null ? "-" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n));
const fmtMonth = (mk) => {
  const [y, m] = (mk || "").split("-").map(Number);
  return new Date(y || 2000, (m || 1) - 1).toLocaleDateString("fr-FR", { month: "short" });
};
const gradeColor = { A: "text-emerald-600", B: "text-blue-600", C: "text-amber-600", D: "text-orange-600", E: "text-red-600" };

export default function Pilotage() {
  const [kpis, setKpis] = useState(null);
  const [health, setHealth] = useState(null);
  const [ca12m, setCa12m] = useState([]);
  const [tresorerie, setTresorerie] = useState(null);
  const [topClients, setTopClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [insight, setInsight] = useState("");
  const [insightLoading, setInsightLoading] = useState(false);
  // ⚠️ Le state du chat vit dans <AIChatCard/> — extrait pour éviter que chaque
  // frappe re-render Pilotage entier (qui recréait de nouveaux tableaux de
  // données pour Recharts → boucle ResizeObserver → « Maximum update depth »).

  // Rapports hebdo
  const [reportCfg, setReportCfg] = useState(null);
  const [reportSaving, setReportSaving] = useState(false);
  const [reportSending, setReportSending] = useState(false);
  const [reportTestEmail, setReportTestEmail] = useState("");
  const DAYS_FR = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
  const HOURS_UTC = [6, 7, 8, 9, 10, 12, 14, 17];

  const loadReportCfg = async () => {
    try {
      const { data } = await api.get("/reports/settings");
      setReportCfg({
        enabled: !!data.enabled, day: data.day ?? 0, hour: data.hour ?? 7,
        recipients: (data.recipients || []).join(", "),
        subject_template: data.subject_template
          || `[${APP_NAME}] Rapport hebdo · {company_name}`,
        last_sent_at: data.last_sent_at, last_status: data.last_status,
      });
    } catch (e) { /* silent — feature-gated */ }
  };
  const saveReportCfg = async () => {
    if (!reportCfg) return;
    setReportSaving(true);
    try {
      const recipients = reportCfg.recipients
        .split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/reports/settings", {
        enabled: reportCfg.enabled, day: Number(reportCfg.day),
        hour: Number(reportCfg.hour), recipients,
        subject_template: reportCfg.subject_template,
      });
      setReportCfg((c) => ({ ...c, ...data,
        recipients: (data.recipients || []).join(", ") }));
      toast.success("Rapports hebdomadaires : configuration enregistrée");
    } catch (e) { toast.error(apiError(e)); }
    finally { setReportSaving(false); }
  };
  const sendReportNow = async () => {
    setReportSending(true);
    try {
      const { data } = await api.post("/reports/send-now");
      const okMsg = `${data.sent}/${data.total} envoi(s) OK`;
      if (data.sent === data.total) toast.success(okMsg);
      else if (data.sent > 0) toast.warning(okMsg);
      else toast.error(okMsg);
      loadReportCfg();
    } catch (e) { toast.error(apiError(e)); }
    finally { setReportSending(false); }
  };
  const testReport = async () => {
    if (!reportTestEmail) return toast.error("Email test requis");
    try {
      const { data } = await api.post("/reports/test",
        { email: reportTestEmail });
      if (data.success) toast.success(`Test envoyé à ${reportTestEmail}`);
      else toast.error(data.error || "Échec test");
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [k, h, c, t, tc, a] = await Promise.all([
          api.get("/pilotage/kpis"),
          api.get("/pilotage/health-score"),
          api.get("/pilotage/charts/ca-12m"),
          api.get("/pilotage/charts/tresorerie-60j"),
          api.get("/pilotage/charts/top-clients"),
          api.get("/pilotage/alerts"),
        ]);
        setKpis(k.data); setHealth(h.data);
        setCa12m(c.data.data || []);
        setTresorerie(t.data);
        setTopClients(tc.data.data || []);
        setAlerts(a.data.alerts || []);
      } catch (e) { toast.error(apiError(e)); }
    })();
    loadReportCfg();
  }, []);

  const runInsight = async () => {
    setInsightLoading(true);
    try {
      const r = await api.get("/pilotage/ai/insight");
      setInsight(r.data.insight);
    } catch (e) { toast.error(apiError(e)); }
    finally { setInsightLoading(false); }
  };

  // Mémoïsation des données Recharts — sans ça, chaque re-render du parent
  // (ex : ouverture d'une carte, ré-hydratation) recrée un nouveau tableau
  // référentiel, ce qui fait re-souscrire le ResizeObserver interne de
  // ResponsiveContainer et peut déclencher une boucle setState/effect.
  const ca12mData = useMemo(
    () => ca12m.map(d => ({ month: fmtMonth(d.month), CA: d.ca })),
    [ca12m],
  );
  const tresoData = useMemo(
    () => (tresorerie?.data || []).map(d => ({
      date: d.date.slice(5), in: d.in, out: -d.out, net: d.net,
    })),
    [tresorerie],
  );

  if (!kpis || !health) return <div className="p-6 text-slate-500">Chargement du pilotage...</div>;

  return (
    <div className="space-y-6" data-testid="pilotage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Pilotage exécutif</h1>
          <p className="text-sm text-slate-500">Vue temps réel cross-modules · assistant IA CFO</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`text-4xl font-bold font-mono ${gradeColor[health.grade]}`}>{health.grade}</div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Health score</p>
            <p className={`text-2xl font-bold font-mono ${gradeColor[health.grade]}`}>{health.score}/100</p>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="CA du mois" value={fmt(kpis.ca_month)} delta={kpis.delta_ca_pct} icon={Euro} accent="blue" />
        <Kpi label="CA cumul annuel" value={fmt(kpis.ca_ytd)} icon={TrendingUp} accent="emerald" />
        <Kpi label="Reste dû clients" value={fmt(kpis.outstanding)} sub={`dont ${fmt(kpis.overdue)} en retard`} icon={AlertTriangle} accent={kpis.overdue > 0 ? "red" : "slate"} />
        <Kpi label="Charges du mois" value={fmt(kpis.achats_month + kpis.engaged_monthly)} sub={`Engagé: ${fmt(kpis.engaged_monthly)}/m`} icon={Activity} accent="amber" />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/40">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-orange-600" />
              <p className="text-sm font-semibold text-orange-900">Alertes ({alerts.length})</p>
            </div>
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-white rounded p-2 text-sm">
                  <span>{a.title}</span>
                  <Badge className={a.severity === "critical" ? "bg-red-100 text-red-700"
                     : a.severity === "high" ? "bg-orange-100 text-orange-700" : "bg-yellow-100 text-yellow-700"}>{a.action}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" />CA 12 mois</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={ca12mData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="CA" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-600" />Trésorerie 60j prévisionnelle</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div><p className="text-xs text-slate-500">Encaissements</p><p className="text-sm font-bold text-emerald-600 font-mono">{fmt(tresorerie?.total_in_60j)}</p></div>
              <div><p className="text-xs text-slate-500">Décaissements</p><p className="text-sm font-bold text-red-600 font-mono">{fmt(tresorerie?.total_out_60j)}</p></div>
              <div><p className="text-xs text-slate-500">Net 60j</p><p className={`text-sm font-bold font-mono ${tresorerie?.net_60j >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(tresorerie?.net_60j)}</p></div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <ComposedChart data={tresoData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={10} />
                <YAxis stroke="#64748b" fontSize={10} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmt(Math.abs(v))} />
                <Bar dataKey="in" fill="#10b981" stackId="cash" name="Entrées" />
                <Bar dataKey="out" fill="#ef4444" stackId="cash" name="Sorties" />
                <Line type="monotone" dataKey="net" stroke="#3b82f6" strokeWidth={2} dot={false} name="Net" />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top clients + Health factors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Top 5 clients</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {topClients.length === 0 ? <p className="text-slate-500 text-sm">Aucune donnée</p> :
             topClients.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate">{i + 1}. {c.name}</span>
                <span className="font-mono font-medium">{fmt(c.ca)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4" />Facteurs santé</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {health.factors.length === 0 ? <p className="text-slate-500 text-sm">Aucun facteur pénalisant · Excellente santé</p> :
             health.factors.map((f, i) => (
              <div key={i} className="flex items-start justify-between text-sm">
                <div>
                  <p className="font-medium">{f.factor}</p>
                  <p className="text-xs text-slate-500">{f.detail}</p>
                </div>
                <Badge className={f.impact >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                  {f.impact >= 0 ? "+" : ""}{f.impact}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* AI Insight */}
      <Card className="border-blue-200 bg-blue-50/30" data-testid="ai-insight-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-600" />Analyse IA hebdomadaire</CardTitle>
          <Button onClick={runInsight} disabled={insightLoading} size="sm" data-testid="run-insight-btn">
            {insightLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Générer
          </Button>
        </CardHeader>
        <CardContent>
          {insight ? (
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line">{insight}</div>
          ) : (
            <p className="text-slate-500 text-sm italic">Cliquez "Générer" pour obtenir une analyse IA de vos KPI actuels.</p>
          )}
        </CardContent>
      </Card>

      {/* Rapports hebdomadaires */}
      {reportCfg && (
        <Card className="border-slate-200" data-testid="reports-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-4 h-4 text-blue-600" />
                Rapport hebdomadaire PDF
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Envoi automatique chaque {DAYS_FR[reportCfg.day]} à {reportCfg.hour}h UTC · KPIs, top clients, alertes
              </p>
            </div>
            <div className="flex items-center gap-2">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/preview`}
                  target="_blank" rel="noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                  data-testid="preview-report-link">Aperçu PDF ↗</a>
              <Button size="sm" variant="outline" onClick={sendReportNow}
                       disabled={reportSending} data-testid="send-report-now-btn">
                {reportSending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Envoyer maintenant
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input id="reports-enabled-toggle" name="reports-enabled-toggle"
                        type="checkbox" checked={reportCfg.enabled}
                        onChange={(e) => setReportCfg({ ...reportCfg, enabled: e.target.checked })}
                        data-testid="reports-enabled-toggle" />
                <span className="font-medium">
                  {reportCfg.enabled ? "Actif" : "Désactivé"}
                </span>
              </label>
              <select id="reports-day-select" name="reports-day-select" value={reportCfg.day}
                       onChange={(e) => setReportCfg({ ...reportCfg, day: Number(e.target.value) })}
                       className="text-sm border rounded px-2 py-1"
                       data-testid="reports-day-select">
                {DAYS_FR.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
              <select id="reports-hour-select" name="reports-hour-select" value={reportCfg.hour}
                       onChange={(e) => setReportCfg({ ...reportCfg, hour: Number(e.target.value) })}
                       className="text-sm border rounded px-2 py-1"
                       data-testid="reports-hour-select">
                {HOURS_UTC.map((h) => <option key={h} value={h}>{`${h}h UTC`}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Destinataires (séparés par virgules)</label>
              <Input value={reportCfg.recipients}
                      onChange={(e) => setReportCfg({ ...reportCfg, recipients: e.target.value })}
                      placeholder="cfo@…, dirigeant@…"
                      data-testid="reports-recipients-input" />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={saveReportCfg} disabled={reportSaving}
                       data-testid="reports-save-btn">
                {reportSaving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Enregistrer
              </Button>
              <Input placeholder="votre@email.com" value={reportTestEmail}
                      onChange={(e) => setReportTestEmail(e.target.value)}
                      className="max-w-xs" data-testid="reports-test-email" />
              <Button size="sm" variant="outline" onClick={testReport}
                       data-testid="reports-test-btn">Envoyer test</Button>
            </div>
            {reportCfg.last_sent_at && (
              <p className="text-xs text-slate-500">
                Dernier envoi : {new Date(reportCfg.last_sent_at).toLocaleString("fr-FR")}
                {reportCfg.last_status && <span className={
                  reportCfg.last_status === "ok" ? "text-emerald-600 ml-2" :
                  reportCfg.last_status === "failed" ? "text-red-600 ml-2" : "text-amber-600 ml-2"
                }>· {reportCfg.last_status}</span>}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Chat — état ISOLÉ dans son propre composant pour ne pas re-render
           les graphes Recharts à chaque frappe. Fix de la boucle
           « Maximum update depth exceeded ». */}
      <AIChatCard />
    </div>
  );
}

// -----------------------------------------------------------------------------
// AIChatCard — carte chat isolée. Toute la state du chat (input, messages,
// loading, sessionId) vit ici, si bien que setState locale ne re-render PAS
// le parent Pilotage (donc pas les 2 ResponsiveContainer Recharts).
// -----------------------------------------------------------------------------
function AIChatCard() {
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const sendChat = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const userMsg = chatInput.trim();
    if (!userMsg || chatLoading) return;
    setChatMessages((m) => [...m, { role: "user", content: userMsg }]);
    setChatInput("");
    setChatLoading(true);
    try {
      const r = await api.post("/pilotage/ai/chat", {
        message: userMsg, session_id: sessionId,
      });
      if (r.data?.session_id) setSessionId(r.data.session_id);
      const reply = typeof r.data?.response === "string" ? r.data.response : "";
      setChatMessages((m) => [
        ...m,
        { role: "assistant", content: reply || "Réponse vide." },
      ]);
    } catch (err) {
      const msg = apiError(err);
      if (msg) toast.error(msg);
      setChatMessages((m) => [
        ...m,
        { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez." },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <Card data-testid="ai-chat-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-600" />
          Assistant IA
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chatMessages.length > 0 && (
          <div className="max-h-64 overflow-y-auto space-y-2 mb-3 bg-slate-50 rounded p-3">
            {chatMessages.map((m, i) => (
              <div
                key={i}
                className={`text-sm ${m.role === "user" ? "text-slate-900 font-medium" : "text-slate-700"}`}
              >
                <strong>{m.role === "user" ? "Vous" : `${APP_NAME} IA`} :</strong>{" "}
                <span className="whitespace-pre-line">{m.content}</span>
              </div>
            ))}
            {chatLoading && (
              <p className="text-xs text-slate-400 italic">Réflexion...</p>
            )}
          </div>
        )}
        <form onSubmit={sendChat} className="flex gap-2">
          <Input
            placeholder="Ex: Quel client me doit le plus ?"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            disabled={chatLoading}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={chatLoading || !chatInput.trim()}
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, sub, delta, icon: Icon, accent }) {
  const bg = { blue: "bg-blue-50", emerald: "bg-emerald-50", red: "bg-red-50", amber: "bg-amber-50", slate: "bg-slate-50" }[accent] || "bg-slate-50";
  const fg = { blue: "text-blue-700", emerald: "text-emerald-700", red: "text-red-700", amber: "text-amber-700", slate: "text-slate-600" }[accent] || "text-slate-600";
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}><Icon className={`w-4 h-4 ${fg}`} /></div>
            {delta != null && (
              <div className={`text-xs font-medium flex items-center gap-1 ${delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {delta >= 0 ? "+" : ""}{delta.toFixed(1)}%
              </div>
            )}
          </div>
          <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
          <p className="text-2xl font-bold font-mono text-slate-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
        </CardContent>
      </Card>
    </motion.div>
  );
}
